import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Loader2, Copy, RefreshCw, Trash2, Users, Link2, CheckCircle2, Info } from "lucide-react";
import { authHeaders, EINLADUNG_STATUS, fmtDate, formatApiErrorDetail } from "@/components/schulungen/roles";
import { StatusBadge } from "@/components/schulungen/FirmenBestellungen";
import FortschrittBalken from "@/components/schulungen/FortschrittBalken";
import { ResetLinkButton } from "@/components/schulungen/ResetLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const einladungsLink = (token) => `${window.location.origin}/schulungen/einladung/${token}`;

function EinladungDialog({ open, onOpenChange, onCreated, plaetzeFrei }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ vorname: "", nachname: "", email: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/schulungen/einladungen`, form, authHeaders(user));
      toast.success(`Einladung für ${data.vorname} ${data.nachname} erstellt`);
      onCreated(data);
      onOpenChange(false);
      setForm({ vorname: "", nachname: "", email: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Einladung fehlgeschlagen"));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="einladung-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Mitarbeiter/in einladen</DialogTitle>
          <DialogDescription>Verbraucht einen freien Platz ({plaetzeFrei} verfügbar). Der Link ist 7 Tage gültig und einmal einlösbar.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Vorname</Label><Input required value={form.vorname} onChange={set("vorname")} className="h-12 rounded-xl" data-testid="einladung-vorname" /></div>
            <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nachname</Label><Input required value={form.nachname} onChange={set("nachname")} className="h-12 rounded-xl" data-testid="einladung-nachname" /></div>
          </div>
          <div><Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail der Mitarbeiterin / des Mitarbeiters</Label><Input type="email" required value={form.email} onChange={set("email")} className="h-12 rounded-xl" data-testid="einladung-email" /></div>
          <Button type="submit" disabled={loading || plaetzeFrei < 1} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 font-semibold" data-testid="einladung-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einladungslink erstellen"}
          </Button>
          {plaetzeFrei < 1 && <p className="text-xs text-red-600 text-center">Keine freien Plätze – bitte zuerst Plätze bestellen.</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EinladungRow({ e, firmenname, onChanged }) {
  const { user } = useAuth();
  const copy = async () => {
    try { await navigator.clipboard.writeText(einladungsLink(e.token)); toast.success("Link kopiert"); }
    catch { toast.error("Kopieren nicht möglich – bitte manuell markieren"); }
  };
  const renew = async () => {
    try { await axios.patch(`${API}/schulungen/einladungen/${e.id}/erneuern`, {}, authHeaders(user)); toast.success("Einladung erneuert (neuer Link, 7 Tage gültig)"); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const revoke = async () => {
    if (!window.confirm(`Einladung für ${e.vorname} ${e.nachname} widerrufen? Der Platz wird wieder frei.`)) return;
    try { await axios.delete(`${API}/schulungen/einladungen/${e.id}`, authHeaders(user)); toast.success("Einladung widerrufen"); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const active = e.status === "offen" || e.status === "abgelaufen";

  return (
    <div className="p-4 sm:p-5 space-y-3" data-testid={`einladung-${e.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-cyan-50 text-cyan-700 font-bold flex items-center justify-center text-sm shrink-0">{e.vorname[0]}{e.nachname[0]}</div>
        <div className="flex-1 min-w-[160px]">
          <p className="font-semibold text-slate-800">{e.vorname} {e.nachname}</p>
          <p className="text-xs text-slate-500">{e.email} · {e.status === "eingeloest" ? `eingelöst am ${fmtDate(e.eingeloest_am)}` : `gültig bis ${fmtDate(e.expires_at)}`}</p>
        </div>
        <StatusBadge map={EINLADUNG_STATUS} status={e.status} />
      </div>
      {active && (
        <div className="flex flex-wrap gap-2">
          {e.status === "offen" && (
            <>
              <button onClick={copy} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-xs font-semibold text-white" data-testid={`copy-${e.id}`}><Copy className="h-3.5 w-3.5" />Einladungslink kopieren</button>
              <span className="text-xs text-slate-500 self-center">Bitte den Link selbst an {e.vorname} weiterleiten (z. B. per E-Mail oder Messenger).</span>
            </>
          )}
          {e.status === "abgelaufen" && (
            <button onClick={renew} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-50 hover:bg-amber-100 text-xs font-semibold text-amber-700" data-testid={`renew-${e.id}`}><RefreshCw className="h-3.5 w-3.5" />Link erneuern</button>
          )}
          <button onClick={revoke} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-slate-400 hover:text-red-600 hover:bg-red-50 ml-auto" data-testid={`revoke-${e.id}`}><Trash2 className="h-3.5 w-3.5" />Widerrufen</button>
        </div>
      )}
    </div>
  );
}

export default function FirmenEinladungen({ firma, onChanged }) {
  const { user } = useAuth();
  const [einladungen, setEinladungen] = useState(null);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/schulungen/einladungen`, authHeaders(user)),
      axios.get(`${API}/schulungen/teilnehmer`, authHeaders(user)),
    ]).then(([e, t]) => { setEinladungen(e.data); setTeilnehmer(t.data); })
      .catch(() => toast.error("Daten konnten nicht geladen werden"));
  }, [user]);

  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-8" data-testid="firmen-einladungen">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-['Manrope'] text-xl font-bold text-slate-900">Einladungen</h2>
            <p className="text-sm text-slate-500">Pro freiem Platz ein persönlicher Link. <span className="font-semibold text-slate-700" data-testid="einladung-plaetze-frei">{firma.plaetze_frei} frei</span> von {firma.plaetze_gesamt}.</p>
          </div>
          <Button onClick={() => setOpen(true)} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-11 px-5 font-semibold" data-testid="portal-invite-btn">
            <UserPlus className="mr-2 h-5 w-5" />Mitarbeiter/in einladen
          </Button>
        </div>
        <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="einladung-hinweis">
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <p><strong>Wichtig:</strong> Ihre Schulungsplätze sind sofort nach der Bestellung verfügbar. Erzeugen Sie hier pro Mitarbeiter/in einen Einladungslink, <strong>kopieren ihn und leiten ihn selbst weiter</strong> – ein automatischer Versand erfolgt nicht. Zertifikate werden erst nach Zahlungseingang Ihrer Rechnung ausgestellt und <strong>per Post an Ihre Firmenadresse</strong> versendet.</p>
        </div>
        <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
          {einladungen === null ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div>
          ) : einladungen.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="einladungen-empty"><Link2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />Noch keine Einladung versendet.</div>
          ) : (
            <div className="divide-y divide-slate-100">{einladungen.map((e) => <EinladungRow key={e.id} e={e} firmenname={firma.firmenname} onChanged={refresh} />)}</div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 flex items-center gap-2"><Users className="h-5 w-5 text-cyan-700" />Teilnehmende <span className="text-sm font-normal text-slate-500">({teilnehmer.length})</span></h2>
        <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
          {teilnehmer.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="teilnehmer-empty">Sobald Mitarbeitende ihre Einladung einlösen, erscheinen sie hier mit Lernfortschritt.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {teilnehmer.map((t) => (
                <div key={t.id} className="p-4 sm:p-5 space-y-4" data-testid={`teilnehmer-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-cyan-50 text-cyan-700 font-bold flex items-center justify-center text-sm shrink-0">{t.vorname[0]}{t.nachname[0]}</div>
                    <div className="flex-1 min-w-0"><p className="font-semibold text-slate-800">{t.vorname} {t.nachname}</p><p className="text-xs text-slate-500 truncate">{t.email} · Zugang seit {fmtDate(t.created_at)}</p></div>
                    <ResetLinkButton endpoint={`/schulungen/teilnehmer/${t.id}/reset-link`} angefordert={!!t.reset_angefordert_am} testId={`reset-btn-${t.id}`} onDone={load} />
                    {t.summary?.zertifikat ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" />{t.summary.zertifikat.versendet_am ? "Zertifikat per Post versendet" : "Zertifiziert – Postversand folgt"}</span> : <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{t.summary?.test_bestanden ? (t.summary?.platz_bezahlt ? "Test bestanden" : "Test bestanden – wartet auf Zahlung") : t.summary?.prozent > 0 ? "In Bearbeitung" : "Noch nicht gestartet"}</span>}
                  </div>
                  <FortschrittBalken teilnehmer={t} compact />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <EinladungDialog open={open} onOpenChange={setOpen} onCreated={refresh} plaetzeFrei={firma.plaetze_frei} />
    </div>
  );
}
